import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import Dashboard from "./Dashboard.jsx";

/* /projects — Cohesion budget-vs-actual view (engineering-owned, session-gated like
   everything else). Lazy: the chunk only loads when the route is visited. */
const CohesionProjects = React.lazy(() => import("./CohesionProjects.jsx"));

/* /_upload — self-service artifact publish page (engineering-owned). Restricted to two
   people, but that gate lives server-side only (bff/src/handler.ts's allowlist check) —
   this renders for any signed-in user, and the page's own first status fetch surfaces a
   403 as "no access" rather than duplicating the allowlist here. */
const UploadArtifact = React.lazy(() => import("./UploadArtifact.jsx"));

/* /milestones — OPAL downstream Project Center milestones + date slippage (ENG-29).
   Engineering-owned, session-gated, and a DIFFERENT data source from /projects. */
const Milestones = React.lazy(() => import("./Milestones.jsx"));

/* Branded splash — identical markup/classes to the static boot splash in index.html,
   so React's takeover is seamless (no flash). */
function Splash({ note }) {
  return (
    <div className="boot-splash">
      <div className="boot-inner">
        <div className="boot-kicker">CoStar Partners · Leadership Priorities</div>
        <h1 className="boot-title">Executive Portfolio Dashboard</h1>
        <div className="boot-dots"><span /><span /><span /></div>
        <div className="boot-note">{note}</div>
      </div>
    </div>
  );
}

/* AuthGate: never render the dashboard until we KNOW the user is signed in. On load we
   ask the BFF /api/auth/me. While checking we show the splash; if signed in we mount the
   dashboard; if not we redirect to login from behind the splash — so the dashboard never
   paints pre-login (no flash). */
function AuthGate() {
  const [state, setState] = useState("checking"); // checking | authed | redirecting | signedout | error
  const [email, setEmail] = useState(null);
  const [errCode, setErrCode] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // After Sign out we land here — show a clean signed-out screen instead of
    // silently bouncing the user straight back in via SSO.
    if (params.get("signed_out")) {
      setState("signedout");
      return;
    }
    // A failed callback bounces back with ?auth_error=… — show a retry instead of looping.
    if (params.get("auth_error")) {
      setErrCode(params.get("auth_error"));
      setState("error");
      return;
    }

    let cancelled = false;
    fetch("/api/auth/me", { credentials: "include" })
      .then(async (r) => {
        if (cancelled) return;
        if (r.ok) {
          const me = await r.json().catch(() => ({}));
          setEmail(me.email || me.displayName || null);
          setState("authed");
          return;
        }
        // Not authenticated → go straight to the OAuth login, keeping the splash up.
        setState("redirecting");
        const here = window.location.pathname + window.location.search;
        window.location.assign(`/api/auth/login?returnTo=${encodeURIComponent(here)}`);
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "authed" && window.location.pathname === "/projects") {
    return (
      <React.Suspense fallback={<Splash note="Loading projects…" />}>
        <CohesionProjects />
      </React.Suspense>
    );
  }
  if (state === "authed" && window.location.pathname === "/milestones") {
    return (
      <React.Suspense fallback={<Splash note="Loading milestones…" />}>
        <Milestones />
      </React.Suspense>
    );
  }
  if (state === "authed" && window.location.pathname === "/_upload") {
    return (
      <React.Suspense fallback={<Splash note="Loading…" />}>
        <UploadArtifact />
      </React.Suspense>
    );
  }
  if (state === "authed") return (
    <>
      <style>{`
        .sh { position:fixed; top:16px; left:50%; transform:translateX(-50%);
          display:flex; align-items:center; gap:10px; z-index:200; pointer-events:auto;
          font-size:12px; color:#cfcdc4; max-width:60%; font-family:'Outfit',sans-serif; }
        .sh-email { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .sh-btn { background:transparent; border:1px solid #4a4a45; color:#cfcdc4;
          border-radius:6px; padding:3px 10px; font-size:11px; cursor:pointer;
          font-family:inherit; white-space:nowrap; flex-shrink:0; }
        .sh-btn:hover { background:#2a2a26; }
        @media (max-width:768px) {
          .sh { position:static; transform:none; left:auto; top:auto;
            max-width:100%; justify-content:center; padding:8px 0; background:#1a1a17; }
        }
      `}</style>
      <div className="sh">
        <span className="sh-email">{email || "Signed in"}</span>
        <button className="sh-btn" onClick={async () => {
          try { await fetch("/api/auth/logout", { method: "POST", credentials: "include" }); } catch {}
          window.location.assign("/?signed_out=1");
        }}>Sign out</button>
      </div>
      <Dashboard />
    </>
  );
  if (state === "redirecting") return <Splash note="Taking you to sign in…" />;
  if (state === "signedout") {
    return (
      <Splash
        note={
          <a className="boot-link" href="/api/auth/login">
            You’re signed out — click to sign in
          </a>
        }
      />
    );
  }
  if (state === "error") {
    // Human wording for the common failure codes the BFF can bounce back with;
    // anything unmapped falls through to the generic line + the raw code below.
    const errText =
      {
        identity_unresolved:
          "Signed in with Atlassian, but we couldn’t confirm your identity — click to try again",
        state_mismatch: "Sign-in session expired mid-login — click to try again",
        missing_params: "The sign-in didn’t complete — click to try again",
      }[errCode] || "Couldn’t sign you in — click to try again";
    return (
      <Splash
        note={
          <>
            <a className="boot-link" href="/api/auth/login">
              {errText}
            </a>
            {errCode && (
              <div style={{ marginTop: 8, fontSize: 11, opacity: 0.5 }}>error: {errCode}</div>
            )}
          </>
        }
      />
    );
  }
  return <Splash note="Loading…" />;
}

createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <AuthGate />
  </React.StrictMode>
);
